/**
 * Payment Service - FIXED VERSION
 * Handles payment recording, tracking, and reconciliation
 */

import { api_service } from './api';
import {
  Payment,
  PaymentCreateData,
  PaymentUpdateData,
  PaymentFilters,
  PaymentReceipt,
  PaymentStatistics,
  CreatePaymentPayload
} from '@/types/payment_types';

// ============ TYPE DEFINITIONS ============

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ============ HELPER FUNCTIONS ============

/**
 * Extract array data from various response formats
 */
function extract_array_from_response<T>(data: any): T[] {
  if (Array.isArray(data)) {
    return data;
  }
  
  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data.results;
  }
  
  if (data && typeof data === 'object') {
    const values = Object.values(data);
    if (values.length > 0 && values.every(v => typeof v === 'string')) {
      console.warn('Received API navigation response instead of payment data');
      return [];
    }
  }
  
  console.warn('Unexpected payment response format, returning empty array');
  return [];
}

// ============ PAYMENT MANAGEMENT ============

/**
 * Fetch all payments with optional filters
 */
export const fetch_payments = async (
  page: number = 1,
  page_size: number = 100
): Promise<Payment[]> => {
  try {
    const response = await api_service.get('/payments/', {
      params: { page, page_size }
    });
    
    console.log('fetch_payments raw response:', response.results);
    const payments = extract_array_from_response<Payment>(response.results);
    console.log('fetch_payments extracted:', payments);
    
    return payments;
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

/**
 * Get paginated list of payments with filters
 */
export const get_payments = async (
  page: number = 1,
  page_size: number = 20,
  filters?: PaymentFilters
): Promise<PaginatedResponse<Payment>> => {
  try {
    const response = await api_service.get('/payments/', {
      params: {
        page,
        page_size,
        ...filters
      }
    });
    
    if (response.data && 'results' in response.data) {
      return response.data;
    }
    
    if (Array.isArray(response.data)) {
      return {
        count: response.data.length,
        next: null,
        previous: null,
        results: response.data
      };
    }
    
    return {
      count: 0,
      next: null,
      previous: null,
      results: []
    };
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

/**
 * Get single payment by ID
 */
export const get_payment = async (id: number): Promise<Payment> => {
  try {
    const response = await api_service.get(`/payments/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment:', error);
    throw error;
  }
};

/**
 * Get payment by reference number
 */
export const get_payment_by_reference = async (
  reference: string
): Promise<Payment> => {
  try {
    const response = await api_service.get(`/payments/by-reference/${reference}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching payment by reference:', error);
    throw error;
  }
};

/**
 * Record new payment - FIXED VERSION
 */
export const create_payment = async (
  data: CreatePaymentPayload | PaymentCreateData
): Promise<Payment> => {
  try {
    let payment_data: any;
    
    if ('invoice_id' in data) {
      // This is CreatePaymentPayload from the form
      // FIXED: Transform to match backend expectations
      payment_data = {
        client: (data as any).client_id, // Client ID from the selected invoice
        invoice: parseInt((data as any).invoice_id as string), // Invoice ID as number
        amount: data.amount,
        payment_date: data.payment_date,
        payment_method: data.payment_method.toUpperCase(), // FIXED: Backend expects uppercase (CASH, MPESA, etc.)
        transaction_reference: (data as any).transaction_id || data.reference_number || '', // M-Pesa or bank reference
        reference_number: data.reference_number || `PAY-${Date.now()}`, // FIXED: Generate if not provided
        notes: data.notes || ''
      };
    } else {
      // This is PaymentCreateData - already in correct format
      payment_data = {
        ...data,
        payment_method: data.payment_method.toUpperCase() // Ensure uppercase
      };
    }

    console.log('Sending payment data to backend:', payment_data);

    const response = await api_service.post('/payments/', payment_data);
    return response.data;
  } catch (error) {
    console.error('Error creating payment:', error);
    throw error;
  }
};

/**
 * Record payment with receipt upload
 */
export const create_payment_with_receipt = async (
  data: PaymentCreateData,
  receipt_file: File,
  on_progress?: (progress: number) => void
): Promise<Payment> => {
  try {
    const form_data = new FormData();
    
    form_data.append('client', data.client.toString());
    form_data.append('amount', data.amount.toString());
    form_data.append('payment_date', data.payment_date);
    form_data.append('payment_method', data.payment_method.toUpperCase()); // FIXED: Uppercase
    
    if (data.invoice) form_data.append('invoice', data.invoice.toString());
    if (data.reference_number) form_data.append('reference_number', data.reference_number);
    if (data.notes) form_data.append('notes', data.notes);
    
    form_data.append('receipt', receipt_file);

    const response = await api_service.post('/payments/', form_data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progress_event) => {
        if (progress_event.total && on_progress) {
          const percentage = Math.round(
            (progress_event.loaded * 100) / progress_event.total
          );
          on_progress(percentage);
        }
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error creating payment with receipt:', error);
    throw error;
  }
};

/**
 * Update existing payment
 */
export const update_payment = async (
  id: number,
  data: PaymentUpdateData
): Promise<Payment> => {
  try {
    const response = await api_service.patch(`/payments/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating payment:', error);
    throw error;
  }
};

/**
 * Delete payment
 */
export const delete_payment = async (id: number): Promise<void> => {
  try {
    await api_service.delete(`/payments/${id}/`);
  } catch (error) {
    console.error('Error deleting payment:', error);
    throw error;
  }
};

/**
 * Void/reverse payment
 */
export const void_payment = async (
  id: number,
  reason: string
): Promise<Payment> => {
  try {
    const response = await api_service.post(`/payments/${id}/void/`, { reason });
    return response.data;
  } catch (error) {
    console.error('Error voiding payment:', error);
    throw error;
  }
};

// ============ PAYMENT ALLOCATION ============

/**
 * Allocate payment to invoice
 */
export const allocate_to_invoice = async (
  payment_id: number,
  invoice_id: number,
  amount?: number
): Promise<Payment> => {
  try {
    const response = await api_service.post(`/payments/${payment_id}/allocate/`, {
      invoice_id,
      amount
    });
    return response.data;
  } catch (error) {
    console.error('Error allocating payment:', error);
    throw error;
  }
};

/**
 * Get unallocated payments
 */
export const get_unallocated_payments = async (): Promise<Payment[]> => {
  try {
    const response = await api_service.get('/payments/unallocated/');
    return extract_array_from_response<Payment>(response.data);
  } catch (error) {
    console.error('Error fetching unallocated payments:', error);
    throw error;
  }
};

// ============ RECEIPTS & DOCUMENTS ============

/**
 * Fetch payment receipt data
 */
export const fetch_payment_receipt = async (
  payment_id_or_data: number | Payment
): Promise<PaymentReceipt> => {
  try {
    let payment: Payment;
    
    // If a full payment object was passed, use it directly
    if (typeof payment_id_or_data === 'object') {
      payment = payment_id_or_data;
    } else {
      // Otherwise fetch from API
      payment = await get_payment(payment_id_or_data);
    }
    
    // Validate we have a payment object
    if (!payment || !payment.reference_number) {
      throw new Error('Invalid payment data received');
    }
    
    // Construct receipt from payment data
    const receipt: PaymentReceipt = {
      payment: payment,
      receipt_number: payment.reference_number || `RCT-${payment.id}`,
      issued_date: new Date().toISOString(),
      client_details: {
        name: payment.client_name,
        email: undefined,
        phone: undefined,
        address: undefined
      },
      company_details: {
        name: 'Water Management System',
        address: 'P.O. Box 1234, Nairobi',
        phone: '+254 700 000000',
        email: 'info@watermanagement.com'
      },
      allocations: payment.allocations?.map(alloc => ({
        invoice_number: alloc.invoice_number,
        invoice_date: alloc.allocated_date,
        amount: alloc.amount
      })) || []
    };

    return receipt;
  } catch (error) {
    console.error('Error fetching payment receipt:', error);
    throw error;
  }
};

/**
 * Download payment receipt as PDF blob 
 */
export const download_payment_receipt_pdf = async (
  payment_id: number
): Promise<Blob> => {
  try {
    console.log('Requesting PDF for payment:', payment_id);
    
    const response = await api_service.get(`/payments/${payment_id}/receipt/`, {
      responseType: 'blob',
    //   headers: {
    //     'Accept': 'application/pdf'
    //   }
    });
    
    console.log('PDF response:', response);
    
    // Handle different response formats
    let blob: Blob;
    
    if (response.data instanceof Blob) {
      blob = response.data;
    } else if (response instanceof Blob) {
      blob = response;
    } else if (response.data) {
      // If data is returned as base64 or other format, convert to blob
      blob = new Blob([response.data], { type: 'application/pdf' });
    } else {
      throw new Error('Invalid response format - expected PDF blob');
    }
    
    // Verify it's actually a PDF
    if (blob.type !== 'application/pdf' && !blob.type.includes('pdf')) {
      console.warn('Response blob type:', blob.type);
      // Try to read the content to see if it's an error message
      const text = await blob.text();
      console.error('Blob content:', text);
      
      if (text.includes('html') || text.includes('json')) {
        throw new Error('Backend returned HTML/JSON instead of PDF. PDF endpoint may not be implemented.');
      }
    }
    
    console.log('PDF blob size:', blob.size, 'bytes');
    
    if (blob.size === 0) {
      throw new Error('Received empty PDF from server');
    }
    
    return blob;
  } catch (error: any) {
    console.error('Error downloading receipt PDF:', error);
    
    // Provide helpful error messages
    if (error.response?.status === 404) {
      throw new Error('PDF generation endpoint not found. Please ensure the backend /payments/{id}/receipt/ endpoint is implemented.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error while generating PDF. Check backend logs.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error('Failed to download receipt PDF');
    }
  }
};
/**
 * Get receipt PDF URL
 */
export const get_receipt_url = (id: number): string => {
  return `/payments/${id}/receipt/`;
};

/**
 * Send receipt via email
 */
export const send_receipt_email = async (
  id: number,
  email_addresses?: string[]
): Promise<{ message: string }> => {
  try {
    const response = await api_service.post(`/payments/${id}/send-receipt/`, {
      email_addresses
    });
    return response.data;
  } catch (error) {
    console.error('Error sending receipt email:', error);
    throw error;
  }
};

// ============ STATISTICS & REPORTS ============

/**
 * Get payment statistics
 */
export const get_stats = async (
  date_from?: string,
  date_to?: string
): Promise<PaymentStatistics> => {
  try {
    const response = await api_service.get('/payments/stats/', {
      params: { date_from, date_to }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching payment stats:', error);
    throw error;
  }
};

/**
 * Get recent payments
 */
export const get_recent_payments = async (
  limit: number = 10
): Promise<Payment[]> => {
  try {
    const response = await api_service.get('/payments/recent/', {
      params: { limit }
    });
    return extract_array_from_response<Payment>(response.data);
  } catch (error) {
    console.error('Error fetching recent payments:', error);
    throw error;
  }
};

// ============ RECONCILIATION ============

/**
 * Get unreconciled payments
 */
export const get_unreconciled_payments = async (): Promise<Payment[]> => {
  try {
    const response = await api_service.get('/payments/unreconciled/');
    return extract_array_from_response<Payment>(response.data);
  } catch (error) {
    console.error('Error fetching unreconciled payments:', error);
    throw error;
  }
};

/**
 * Mark payment as reconciled
 */
export const mark_as_reconciled = async (
  id: number,
  reconciliation_notes?: string
): Promise<Payment> => {
  try {
    const response = await api_service.post(`/payments/${id}/reconcile/`, {
      reconciliation_notes
    });
    return response.data;
  } catch (error) {
    console.error('Error reconciling payment:', error);
    throw error;
  }
};

// ============ VALIDATION ============

/**
 * Validate payment reference number
 */
export const validate_reference_number = async (
  reference: string,
  exclude_id?: number
): Promise<{ is_valid: boolean; message?: string }> => {
  try {
    const response = await api_service.get('/payments/validate-reference/', {
      params: {
        reference,
        exclude: exclude_id
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error validating reference number:', error);
    throw error;
  }
};

// ============ CLASS-BASED SERVICE ============

class PaymentService {
  get_payments = get_payments;
  fetch_payments = fetch_payments;
  get_payment = get_payment;
  get_payment_by_reference = get_payment_by_reference;
  create_payment = create_payment;
  create_payment_with_receipt = create_payment_with_receipt;
  update_payment = update_payment;
  delete_payment = delete_payment;
  void_payment = void_payment;
  allocate_to_invoice = allocate_to_invoice;
  get_unallocated_payments = get_unallocated_payments;
  fetch_payment_receipt = fetch_payment_receipt;
  download_payment_receipt_pdf = download_payment_receipt_pdf;
  get_receipt_url = get_receipt_url;
  send_receipt_email = send_receipt_email;
  get_stats = get_stats;
  get_recent_payments = get_recent_payments;
  get_unreconciled_payments = get_unreconciled_payments;
  mark_as_reconciled = mark_as_reconciled;
  validate_reference_number = validate_reference_number;
}

// Export singleton instance
export const payment_service = new PaymentService();

// Default export
export default payment_service;